/**
 * ProjectAttentionProvider.tsx — один список «Требует внимания» на открытый
 * проект (WEB-3782): его читают блок главной (`AttentionBlock` с пропом
 * `state`) и число у пункта «Главная» в боковом меню (`Sidebar.tsx`). Один
 * запрос на проект, а не по запросу у блока и у меню: хук `useAttentionItems`
 * держит состояние у себя, общего кэша нет.
 *
 * Почему каркас. Раньше над каждой страницей проекта висели четыре плашки
 * каркаса — долг (`PastDueBanner`), идущая генерация
 * (`ActiveGenerationBanner`), поломка доставки во внешнюю аналитику
 * (`AnalyticsDeliveryBanner`) и старый адрес (`LegacyAddressBanner`).
 * Решение владельца 23.10 и 24.10 (6а–6в): плашки каркаса переезжают в блок
 * «Требует внимания» на главной, а на любом экране о них говорит число у
 * «Главной» в меню. Отсюда поставщик в каркасе (`DashboardLayout`), над меню
 * и страницей. Плашка заморозки проекта остаётся в оболочке (WEB-3888).
 *
 * Обновление списка:
 *  · смена проекта — новый запрос, данные прежнего проекта под новым не
 *    видны (это держит сам хук);
 *  · приход на главную (`PROJECT_LANDING`) с другой страницы — `refresh()`;
 *    первый заход сразу на главную — один запрос, без повтора; приход на
 *    главную вместе со сменой проекта — тоже один запрос;
 *  · сигнал «подключение Amplitude сохранено или проверено»
 *    (`amplitudeHealthSignal.tsx`, WEB-2413 п.9) — `refresh()`: раньше его
 *    слушала плашка доставки, теперь — список (строка Amplitude в блоке
 *    уходит сразу после починки ключа).
 * Опроса по таймеру нет. Режим организации или проекта нет — `scope = null`,
 * запросов нет. Без поставщика `useProjectAttention()` — пустое состояние.
 */
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@/hooks/hooks';
import { useAmplitudeHealthVersion } from '@/components/analytics-integrations/amplitudeHealthSignal';
import { PROJECT_LANDING } from '@/router/landingRoute';
import {
  useAttentionItems,
  type AttentionItemsState,
} from './useAttentionItems';

const noop = () => {};
const EMPTY: AttentionItemsState = {
  data: null,
  loading: false,
  error: null,
  refresh: noop,
};

const ProjectAttentionContext = createContext<AttentionItemsState | null>(null);

function isHome(pathname: string): boolean {
  return (
    pathname === PROJECT_LANDING || pathname.startsWith(PROJECT_LANDING + '/')
  );
}

export function ProjectAttentionProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  // Без среза проектов (каркас в отдельном тесте) — как режим организации.
  const projectId = useAppSelector((state) =>
    !state.projects || state.projects.orgMode
      ? null
      : (state.projects.currentProject?.id ?? null),
  );
  const state = useAttentionItems(
    projectId ? { kind: 'project', projectId } : null,
  );
  const { refresh } = state;
  const { pathname } = useLocation();
  const onHome = isHome(pathname);
  const version = useAmplitudeHealthVersion();

  // Приход на главную с другой страницы того же проекта — свежий список.
  const seen = useRef({ onHome, projectId, version });
  useEffect(() => {
    const prev = seen.current;
    seen.current = { onHome, projectId, version };
    if (projectId === null || prev.projectId !== projectId) return;
    if ((onHome && !prev.onHome) || version !== prev.version) refresh();
  }, [onHome, projectId, version, refresh]);

  return (
    <ProjectAttentionContext.Provider value={state}>
      {children}
    </ProjectAttentionContext.Provider>
  );
}

/** Список открытого проекта; без поставщика — пустое состояние, запросов нет. */
export function useProjectAttention(): AttentionItemsState {
  return useContext(ProjectAttentionContext) ?? EMPTY;
}
